import { Component, ViewChild } from "@angular/core";
import { ImportsModule } from "../imports";

import { ScopeService } from "../services/scope.service";
import { MessagingService } from "../services/messaging.service";
import { Table } from "primeng/table";

interface Column {
  actions?: any[];
  key?: boolean;
  label: string;
  field: string;
  width?: number;
  align?: string;
  options?: any[];
  filterValue?: any;
  maxLength?: number,
  upperCase?: boolean;
  hidden?: boolean;
  filterType?: string;
  matchMode?: string;
  editable?: boolean;
  title?: string;
  getText?: boolean;
}

@Component({
  selector: 'app-all-libraries',
  standalone: true,
  imports: [ImportsModule],
  templateUrl: './all-libraries.component.html',
  styleUrl: './all-libraries.component.css'
})
export class AllLibrariesComponent {
  @ViewChild('dt') dataTable!: Table;
  refreshButtonText: { label: string; }[] = [{ label: "Refresh" }, { label: "Clear Filters" }];
  appAreas!: { label: string, value: any; }[];
  categories: any[] = [{ label: 'System', value: 1 }, { label: '3rd Party', value: 2 }, { label: 'Application', value: 3 }, { label: 'Other', value: null }];
  types: any[] = [{ label: 'Targeted', value: 3 }, { label: 'For Reference', value: 2 }, { label: "Excluded", value: 1 }];
  // types: any[] = [{ label: 'In Scope', value: 3 }, { label: 'For Reference', value: 2 }, { label: "Excluded", value: 1 }];
  lazyRows: number = 100;
  isFiltered: boolean = false;
  columns!: Column[];
  items: any[] = [];
  filteredItems: any[] = [];
  totalRecords!: number;
  isBusy: boolean = true;
  filters: any = {};
  defaultFilters: any = {};
  sortedColumns: any[] = [{ field: 'name', order: 1 }];
  massEditVisible: boolean = false;
  massEditProps!: any[];
  private refreshing: boolean = false;
  private isInitEditing: boolean = false;

  constructor(private scopingService: ScopeService, private messagingService: MessagingService) {
  }

  ngOnInit() {
    this.scopingService.getAppAreas({ orderby: "area" }).subscribe({
      next: (data: any) => {
        this.appAreas = data.items.map((i: any) => {
          if (i.text) return { label: `${i.area} - ${i.text}`, value: i.id };
          else return { label: i.area, value: i.id };
        });
        this.appAreas.push({ label: "Other", value: null });
        this.columns = [
          { field: "name", label: "Name", width: 220, maxLength: 10, upperCase: true, key: true },
          { field: "text", label: "Text", width: 400, maxLength: 50, matchMode: "contains" },
          { field: "category", label: "Type", width: 200, options: this.categories, matchMode: "in", editable: true, getText: true },
          { field: "type", label: "Analysis Type", width: 200, options: this.types, matchMode: "in", editable: true, getText: true },
          { field: "appArea", label: "Application Area", width: 250, options: this.appAreas, matchMode: "in", editable: true, getText: true },
          { field: "notes", label: "Notes", width: 400, maxLength: 3000, matchMode: "contains", editable: true },
        ];
        this.refresh();

        this.massEditProps = [
          { field: "category", label: "Replace Type", options: this.categories, change: false, value: null, default: null },
          { field: "type", label: "Replace Analysis Type", options: this.types, change: false, value: null, default: 2 },
          { field: "appArea", label: "Replace Application Area", options: this.appAreas, change: false, value: null, default: null },
          { field: "notes", label: "Replace Notes", change: false, maxLength: 3000, multiline: true, value: null, default: null }
        ];

      },
      error: (error: any) => {
        this.messagingService.showHttpError("error", error.error, "Failed to load data.", error);
      }
    });
  }

  refresh() {
    this.items.length = 0;
    this.isBusy = true;
    this.refreshing = true;
    if (this.dataTable) this.dataTable.resetScrollTop();
    this.loadData();
  }

  getFilter(withOrderby?: boolean): any {
    // We only want to show them scoped libraries (for now) and programs
    const filters = JSON.parse(JSON.stringify(this.defaultFilters));

    // Add/replace more filters based on user input
    this.isFiltered = false;
    if (this.filters) {
      for (const field in this.filters) {
        const filter: any = this.filters[field] || null;
        if (!filter) continue;
        if (filter.value == null) continue;
        if (this.refreshing) {
          const defaultValue = this.defaultFilters[filter.name]?.value || null;
          const column = this.columns.find((c: Column) => c.field === field);
          if (column && column.filterValue) column.filterValue = defaultValue;
          filter.value = defaultValue;
        } else {
          if (typeof filter.value === "string" && filter.value.length == 0) filter.value = null;
          if (typeof filter.value === "number" && isNaN(filter.value)) filter.value = null;
          if (Array.isArray(filter.value) && !filter.value.length) filter.value = null;
          const column = this.columns.find((c: any) => c.field === field);
          if (column?.upperCase && typeof filter.value == "string") filter.value = filter.value.toUpperCase();
          if (column?.filterType === "numeric" && typeof filter.value == "number") filter.value = parseInt(filter.value);
          if (filter.value == null) continue;
          this.isFiltered = true;
          filters[field] = Object.assign({}, this.filters[field]);
        }
      }
    }

    if (this.refreshing) this.refreshing = false;

    // Add sorting
    let orderby: any = undefined;
    if (withOrderby) {
      if (this.sortedColumns.length) {
        orderby = [];
        for (const column of this.sortedColumns) {
          const col = this.columns.find((c: any) => c.field === column.field);
          if (col) {
            orderby.push({ field: col.field, desc: column.order < 0 ? true : undefined });
          }
        }
      };
    }

    const result: any = { filters: filters, orderby: orderby };

    return result;
  }

  loadData() {
    const filter = this.getFilter(true);
    filter.select = this.columns.filter(i => i.field).map((c: any) => c.field);

    this.scopingService.getLibraries(filter).subscribe({
      next: (data: any) => {
        this.filteredItems = this.items = data.items.filter((i: any) => i.name != 'QTEMP' && i.type < 4);
        this.totalRecords = this.items.length;
      },
      error: (error: any) => {
        this.messagingService.showHttpError("error", error.error, "Failed to load data.", error);
      },
      complete: () => {
        this.isBusy = false;
      }
    });
  }

  getLibTypeLabel(libType: number) {
    return this.types.find((lt: any) => lt.value === libType)?.label;
  }

  updateRecord(data: any) {
    // Only send the fields that are key or editable
    const fields = this.columns.filter((c: any) => c.key | c.editable);
    const changedObject: any = {};
    for (const field of fields) {
      changedObject[field.field] = data[field.field];
    }
    changedObject.lastChangedBy = "user";
    changedObject.lastChangedDate = new Date();

    this.scopingService.updateLibrary(changedObject).subscribe({
      next: (result: any) => {
        const item = this.items.find((i: any) => i.name === data.name);
        if (item) {
          for (const field of fields) {
            item[field.field] = data[field.field];
          }
        }
      },
      error: (error: any) => {
        this.messagingService.showHttpError("error", error.error, "Failed to update record.", error);
      }
    });
  }

  exportExcel() {
    this.isBusy = true;
    const filter = this.getFilter(true);
    filter.select = this.columns.filter(i => i.field).map((c: any) => c.field);
    this.scopingService.getLibraries(filter).subscribe({
      next: (data: any) => {
        const items: any[] = data.items || data || [];
        import("xlsx").then(xlsx => {
          const worksheet = xlsx.utils.json_to_sheet(items);
          const workbook = { Sheets: { data: worksheet }, SheetNames: ["data"] };
          const excelBuffer: any = xlsx.write(workbook, {
            bookType: "xlsx",
            type: "array"
          });
          this.saveAsExcelFile(excelBuffer, "libraries");
        });
      }
    });
  }

  saveAsExcelFile(buffer: any, fileName: string): void {
    import("file-saver-es").then(FileSaver => {
      this.isBusy = false;
      let EXCEL_TYPE =
        "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet;charset=UTF-8";
      let EXCEL_EXTENSION = ".xlsx";
      const data: Blob = new Blob([buffer], {
        type: EXCEL_TYPE
      });
      FileSaver.saveAs(
        data,
        fileName + EXCEL_EXTENSION
      );
    });
  };

  handleSort(event: any) {
    this.sortedColumns = event.multisortmeta || event.multiSortMeta;
  }

  handleFilter(event: any) {
    this.filteredItems = event.filteredValue;
    this.totalRecords = event.filteredValue.length;
  }

  getRefreshText() {
    this.getFilter();
    return !this.isFiltered ? this.refreshButtonText[0].label : this.refreshButtonText[1].label;
  }

  showMassEdit() {
    this.massEditProps.filter(p => p.change = false);
    this.massEditProps.filter(p => p.value = p.default);
    this.massEditVisible = true;
  }

  canDoMassUpdate() {
    return this.massEditProps.some((p: any) => p.change);
  }

  doMassUpdate() {
    this.isBusy = true;
    const parms = this.getFilter();
    parms.newValues = {};
    const newValues = this.massEditProps.filter((p: any) => p.change);
    for (const field of newValues) parms.newValues[field.field] = field.value;

    this.scopingService.massUpdateLibraries(parms).subscribe({
      next: (data: any) => {
        this.messagingService.showSuccess(`Mass update ${data.rowsAffected} items.`);
        for (const field in parms.newValues) {
          this.filteredItems.filter(i => i[field] = parms.newValues[field]);
        }
      },
      error: (error: any) => {
        this.messagingService.showHttpError("error", error.error, "Failed to load data.", error);
      },
      complete: () => {
        this.isBusy = false;
        this.massEditVisible = false;
      }
    });
  }

  getEditingItemsText(prefix: string) {
    if (this.filteredItems.length === this.items.length) return prefix + ' ALL items';
    else {
      let result = prefix + ` ${this.filteredItems.length} item`;
      if (this.filteredItems.length !== 1) result += "s";
      return result;
    }
  }

  doAction(action: any, data: any) {
    if (typeof action == "function") action.call(this, data);
  }

  getText(item: any, column: any) {
    return column.options.find((o: any) => o.value === item[column.field])?.label;
  }

  onEditInit(event: any) {
    this.isInitEditing = true;
  }
  onEditComplete(event: any) {
    this.isInitEditing = false;
  }

  dropDownOnFocus(dropDown: any) {
    if (this.isInitEditing && dropDown.focused) {
      this.isInitEditing = false;
      dropDown.hide();
      dropDown.show();
    }
  }
}
